import { useState, useEffect } from 'react';

/**
 * Custom hook for managing turn timer countdown
 * @param {boolean} yourTurn - Whether it's the player's turn
 * @param {Object} gameState - Current game state
 * @returns {Object} - Timer state and utilities
 */
export const useTurnTimer = (yourTurn, gameState) => {
  const [turnTimeLeft, setTurnTimeLeft] = useState(null);

  useEffect(() => {
    // If it's not your turn, don't show timer
    if (!yourTurn) {
      setTurnTimeLeft(null);
      return;
    }

    // Check if timer is disabled (turnDuration is 0 or undefined means no timer)
    const turnDuration = gameState?.turnDuration;
    if (!turnDuration || turnDuration === 0) {
      setTurnTimeLeft(null);
      return;
    }

    // If turnStartTime is not set, don't show timer
    if (!gameState?.turnStartTime) {
      setTurnTimeLeft(null);
      return;
    }

    const startTime = gameState.turnStartTime;
    const warningTime = 15000; // Show countdown when 15 seconds left
    
    const updateTimer = () => {
      const now = Date.now();
      const elapsed = now - startTime;
      const remaining = turnDuration - elapsed;
      
      if (remaining <= 0) {
        setTurnTimeLeft(null);
        return;
      }
      
      // Only show countdown when 15 seconds or less remain
      if (remaining <= warningTime) {
        setTurnTimeLeft(Math.ceil(remaining / 1000));
      } else {
        setTurnTimeLeft(null);
      }
    };

    // Update immediately
    updateTimer();
    
    // Update every second
    const interval = setInterval(updateTimer, 1000);
    
    return () => clearInterval(interval);
  }, [yourTurn, gameState?.turnStartTime, gameState?.turnDuration]);

  return {
    turnTimeLeft,
    isWarning: turnTimeLeft !== null && turnTimeLeft <= 15,
    shouldShowTimer: turnTimeLeft !== null && turnTimeLeft <= 15
  };
};

/**
 * Turn timer display component
 * @param {number} turnTimeLeft - Time left in seconds
 * @param {Object} style - Additional styles to apply
 * @returns {JSX.Element} - Timer display component
 */
export const TurnTimerDisplay = ({ turnTimeLeft, style = {} }) => {
  if (turnTimeLeft === null || turnTimeLeft > 15) {
    return null;
  }

  const defaultStyle = {
    position: "absolute",
    top: "20px",
    left: "50%",
    transform: "translateX(-50%)",
    zIndex: 1001,
    backgroundColor: "rgba(255, 0, 0, 0.8)",
    color: "white",
    padding: "10px 20px",
    borderRadius: "8px",
    fontSize: "18px",
    fontWeight: "bold",
    textAlign: "center",
    border: "2px solid #ff0000",
    boxShadow: "0 0 10px rgba(255, 0, 0, 0.5)",
    ...style
  };

  return (
    <div style={defaultStyle}>
      {turnTimeLeft}s
    </div>
  );
};
